<template>
  <tbody>
    <!-- Group Header -->
    <tr v-if="hasProducts || isParentWithChildren" class="group">
      <td colspan="6"
        :class="[
          'py-3 bg-gray-50 transition-colors',
          isParent || groupId === 1 ? 'cursor-pointer hover:bg-gray-100 px-4' : 'px-4'
        ]"
        @click="handleHeaderClick()">
        <div class="flex items-center justify-between">
          <!-- Main Category Header (larger font) -->
          <h3 v-if="isParent" class="text-lg font-bold text-gray-900">{{ $t(groupName) }}</h3>
          <!-- Subcategory / product family header: left-aligned and bold (feedback #10) -->
          <h4 v-else class="text-base font-bold text-gray-900">{{ $t(groupName) }}</h4>

          <div class="flex items-center gap-2">
            <span v-if="totalGroupQuantityIncludingChildren > 0" class="text-sm text-gray-600 font-medium">
              ({{ totalGroupQuantityIncludingChildren }} {{ $t('Artikel') }})
            </span>
            <!-- Only show collapse arrow for parent categories or category 1 -->
            <svg
              v-if="isParent || groupId === 1"
              :class="['w-5 h-5 text-gray-400 transition-transform duration-200', { 'rotate-180': currentExpandedState }]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </td>
    </tr>

    <!-- Collapsible Content -->
    <template v-if="hasProducts && currentExpandedState">
      <!-- Minimum Quantity Warning -->
      <tr v-if="minQuantity > 0">
        <td colspan="6" class="px-4 py-2">
          <p class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            {{ $t('The minimum order quantity for the light carafes and adventure mounts is :quantity pieces in total (not per unit). You can freely choose the SKUs within the group.', { quantity: minQuantity }) }}
          </p>
        </td>
      </tr>

      <!-- Product Rows -->
      <CartProductRow
        v-for="product in groupProducts"
        :key="product.sku"
        :product="product"
        :quantity="quantities[product.sku] || 0"
        :currency="currency"
        :pricing-tiers="pricingTiers"
        :product-tier-pricing="productTierPricing"
        :current-pricing-tier="currentPricingTier"
        :tax-rate="taxRate"
        @update-quantity="handleUpdateQuantity"
        @show-pricing-modal="$emit('show-pricing-modal')"
        @show-fixed-price-modal="$emit('show-fixed-price-modal')"
      />
    </template>
  </tbody>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import CartProductRow from './CartProductRow.vue'

interface Product {
  sku: string
  name: string
  categoryId: number
  productUrl?: string
  description: string
  image?: string
  price?: { value: number; currency: string }
  fixedNetPrice?: { value: number; currency: string }
  fixedNetPriceLaunch?: { value: number; currency: string }
  excludedFromDiscounts?: boolean
  deliveryInWeeksFrom?: number
  deliveryInWeeksTo?: number
  availableQuantities: number[]
  isPos?: boolean
}


interface PosConfig {
  freeMaterialsTier1: number
  freeMaterialsTier2Plus: number
  additionalItemFee: number
  materialGroupId: number
  tosUrl: string
}

interface Props {
  groupId: number
  groupName: string
  isParent?: boolean
  isCollapsed?: boolean
  childCategoryIds?: number[]
  products: Product[]
  quantities: Record<string, number>
  currency: string
  pricingTiers: PartnerPortal.Xhosa.Contracts.DTOs.ResellerPricingTierDto[]
  productTierPricing: Record<string, Record<string, number>>
  currentPricingTier: PartnerPortal.Xhosa.Contracts.DTOs.ResellerPricingTierDto
  taxRate: number
  minQuantity?: number
  posConfig?: PosConfig
}

interface Emits {
  (e: 'update-quantity', sku: string, quantity: number): void
  (e: 'show-pricing-modal'): void
  (e: 'show-fixed-price-modal'): void
  (e: 'toggle-collapse'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// State for collapse/expand - Only for non-parent categories (subcategories)
// Parent categories use the isCollapsed prop from parent component
const isExpanded = ref(false)

const groupProducts = computed(() => {
  // Special case: groupId -1 means show all products (when no categories available)
  if (props.groupId === -1) {
    return props.products
  }

  return props.products.filter(product => {
    return product.categoryId === props.groupId
  })
})

const hasProducts = computed(() => {
  return groupProducts.value.length > 0
})

// Check if this is a parent category header (with no products itself)
// We show parent headers even if they have no products, as long as they have children
const isParentWithChildren = computed(() => {
  return props.isParent === true && !hasProducts.value
})

// Calculate total quantity for this group
const totalGroupQuantity = computed(() => {
  return groupProducts.value.reduce((total, product) => {
    return total + (props.quantities[product.sku] || 0)
  }, 0)
})

// Calculate total quantity including child categories (for parent category headers)
const totalGroupQuantityIncludingChildren = computed(() => {
  if (!props.isParent || !props.childCategoryIds || props.childCategoryIds.length === 0) {
    return totalGroupQuantity.value
  }
  // For parent categories: sum products in this category AND all child categories
  const allCategoryIds = [props.groupId, ...props.childCategoryIds]
  return props.products
    .filter(product => allCategoryIds.includes(product.categoryId))
    .reduce((total, product) => total + (props.quantities[product.sku] || 0), 0)
})

// Current expanded state - uses isCollapsed prop for parents, always true for subcategories
const currentExpandedState = computed(() => {
  if (props.isParent) {
    // Parent categories: inverted isCollapsed prop (collapsed = false means expanded = true)
    return !props.isCollapsed
  }
  if (props.groupId === 1) {
    // Category 1 (Solar Lanterns): use internal isExpanded state
    return isExpanded.value
  }
  // Subcategories: always expanded (not collapsible)
  return true
})

// Handle header click - emit event for parents, toggle internal state for subcategories
const handleHeaderClick = () => {
  if (props.isParent) {
    // Parent category: emit toggle-collapse event
    emit('toggle-collapse')
  } else if (props.groupId === 1) {
    // Category 1 (Solar Lanterns): toggle internal state
    isExpanded.value = !isExpanded.value
  }
  // Subcategories (non-parent, non-category-1): do nothing (not collapsible)
}

// Auto-expand groups that have items with quantity > 0 on mount
// Category 1 (Solar Lanterns) and "All Products" (-1) are always expanded
onMounted(() => {
  if (props.groupId === 1 || props.groupId === -1 || totalGroupQuantity.value > 0) {
    isExpanded.value = true
  }
})

const handleUpdateQuantity = (sku: string, quantity: number) => {
  emit('update-quantity', sku, quantity)
}
</script>